<!--
There are several different presets for this component.
Either a preset must be selected via the "version" attribute, or all of the
following attributes must be provided:
- headline
- button-text
- button-link
- image-url

Example: <image-cta-small version='give-get'></image-cta-small>

The possible values for `version` are:
- 'help-your-friends'
- 'friends-dont-let-friends'
- 'give-get'
- 'rooftop-solar'
- 'heat-pump'

Presets may define the `headline` text, `body` html, `button` text, `link` URL,
and `image` component.
To see the values for each preset, look at the contents of `data()` in the
script section of this file.
-->

<template>
    <div
        class="image-cta-small"
        :style="extraStyle"
    >
        <div class="d-flex bg-white p-3 px-sm-4 border border-gray-200 rounded card-shadow">
            <div class="order-1">
                <div class="d-flex justify-content-between mb-3 mb-sm-1">
                    <!-- headline -->
                    <div class="header align-self-center order-1">
                        <h4>
                            <slot>
                                <span v-if="headline">
                                    {{ headline }}
                                </span>
                                <span v-else-if="version">
                                    {{ presets[version].headline }}
                                </span>
                            </slot>
                        </h4>
                    </div>
                    <!-- Image (xs) -->
                    <div
                        class="d-block d-sm-none flex-shrink-0 icon"
                        :class="imageRight ? 'order-2 ml-3' : 'order-0 mr-3'"
                    >
                        <img
                            v-if="imageUrl"
                            :src="imageUrl"
                        >
                        <component
                            :is="presets[version].image"
                            v-else-if="version && presets[version].image"
                        />
                    </div>
                </div>

                <!-- Optional body text -->
                <!-- eslint-disable vue/no-v-html -->
                <div
                    v-if="bodyHtml || version && presets[version].body"
                    class="mb-2"
                >
                    <!-- eslint-disable vue/no-v-html -->
                    <span
                        v-if="bodyHtml"
                        v-html="bodyHtml"
                    />
                    <span
                        v-else
                        v-html="presets[version].body"
                    />
                </div> <!-- Optional body text -->

                <form
                    ref="ctaForm"
                    method="GET"
                    class="mb-0 text-left d-lg-flex align-items-end"
                    :action="buttonLink || presets[version].link"
                    @submit.prevent="validateAndSubmit"
                >
                    <div v-if="rc !== ''">
                        <input
                            name="rc"
                            type="hidden"
                            :value="rc"
                        >
                    </div>

                    <!-- Zip entry section -->
                    <div
                        v-if="zipEntry"
                        class="d-block flex-shrink-1"
                    >
                        <div class="zip-entry-wrapper">
                            <es-input
                                :id="zipCodeInputId"
                                v-model="zipCode"
                                name="zip_code"
                                input-class="zip-entry bg-gray-100 border-gray-500"
                                :validator="$v.zipCode"
                                placeholder="5-digit zip code"
                                label="Enter your zip code"
                                bold-label
                            >
                                <template #afterInput>
                                    <location-icon
                                        class="location-icon"
                                        aria-hidden="true"
                                    />
                                </template>
                                <template #validationText>
                                    This field is required
                                </template>
                            </es-input>
                        </div>
                    </div> <!-- Zip entry section -->

                    <div class="d-flex flex-lg-grow-1">
                        <es-button
                            class="cta-button flex-grow-1 text-nowrap"
                            :class="zipEntry ? 'flex-md-grow-0 flex-lg-grow-1' : 'flex-sm-grow-0'"
                            type="submit"
                        >
                            <span v-if="buttonText">
                                {{ buttonText }}
                            </span>
                            <span v-else>
                                {{ presets[version].button }}
                            </span>
                        </es-button>
                    </div>
                </form>
            </div>

            <!-- Image (> xs) -->
            <div
                class="d-none d-sm-block align-self-center flex-shrink-0 icon"
                :class="imageRight ? 'order-2 ml-3' : 'order-0 mr-3'"
            >
                <img
                    v-if="imageUrl"
                    :src="imageUrl"
                >
                <component
                    :is="presets[version].image"
                    v-else-if="version && presets[version].image"
                />
            </div>
        </div>
    </div>
</template>

<script>
import { required, minLength } from 'vuelidate/lib/validators';
import { validationMixin } from '@/utils/mixins';
import { makeIdUnique } from '@/utils/functions';
import EsButton from '@/components/EsButton';
import EsInput from '@/components/EsInput';
import LocationIcon from '@/components/icons/LocationIcon';
import BlogImage from '@/components/images/Blog';
import GiftCardImage from '@/components/images/GiftCard';
import HeatPumpImage from '@/components/images/HeatPump';
import ReferAFriendImage from '@/components/images/ReferAFriend';

export default {
    components: {
        EsButton,
        EsInput,
        LocationIcon,
        BlogImage,
        GiftCardImage,
        HeatPumpImage,
        ReferAFriendImage,
    },
    mixins: [validationMixin],
    props: {
        headline: {
            type: String,
            required: false,
            default: '',
        },
        bodyHtml: {
            type: String,
            required: false,
            default: '',
        },
        buttonText: {
            type: String,
            required: false,
            default: '',
        },
        buttonLink: {
            type: String,
            required: false,
            default: '',
        },
        version: {
            type: String,
            required: false,
            default: '',
        },
        imageUrl: {
            type: String,
            required: false,
            default: '',
        },
        rc: {
            type: String,
            required: false,
            default: '',
        },
        extraStyle: {
            type: String,
            required: false,
            default: '',
        },
        zipEntry: {
            type: Boolean,
            required: false,
            default: false,
        },
        imageRight: {
            type: Boolean,
            required: false,
            default: false,
        },
    },
    data() {
        return {
            zipCode: '',
            zipCodeInputId: makeIdUnique('zip_code'),
        };
    },
    methods: {
        validateAndSubmit(event) {
            this.$v.$touch();
            if (this.zipEntry && this.$v.$error) {
                return;
            }
            // TODO: this is carried over from a specific use case, but in the
            // future may be adapted to have custom tracking for various CTAs
            if (window.gaTrackingView === 'Summary') {
                window.gaEvent('summary_cta_referral_link', 'click', 'referral_link');
            }
            event.target.submit();
        },
    },
    validations: {
        zipCode: {
            required,
            minLength: minLength(5),
        },
    },
};
</script>

<style lang="scss" scoped>
@import "@/sass/bs4/_helpers.scss";

.image-cta-small {
    max-width: 640px;
    @include media-breakpoint-down(xs) {
        max-width: 320px!important;
    }

    :deep() {
        @import "@/sass/widgets.scss";
        font-family: $font-family-sans-serif;
        font-size: $font-size-base;
    }
}

.header {
    h4 {
        line-height: 1.4;
        font-size: 18px;

        @include media-breakpoint-down(xs) {
            font-size: 20px;
        }
    }
    @include media-breakpoint-down(xs) {
        max-width: 200px;
    }
}

.card-shadow {
    box-shadow: 4px 5px 8px 0 rgba(86, 86, 86, 0.17);
}

.icon {
    height: 120px;
    width: 120px;

    @include media-breakpoint-down(xs) {
        height: 80px;
        width: 80px;
    }
}

.location-icon {
    position: absolute;
    right: 8px;
}

.cta-button {
    min-width: 185px;
}

.zip-entry-wrapper .form-group {
    margin-bottom: .5rem;
    margin-right: 0;
    @include media-breakpoint-up(lg) {
        margin-bottom: 0;
        margin-right: .5rem;
    }
}
</style>
